import { useBestPracticeCheckResultDialog } from "@common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultDialog.hooks.ts";
import BestPracticeCheckResultView from "@common/components/bestPracticeCheckResultDialog/BestPracticeCheckResultView.tsx";
import { BestPracticeCheckViewModel } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.types.ts";
import { useBestPracticeCheckDialog } from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialog.hooks.ts";
import BestPracticeCheckDialogContent from "@common/components/bestPracticeCheckDialog/BestPracticeCheckDialogContent.tsx";
import { DotLegalAlert, DotLegalDialog, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { Box } from "@mui/material";
import AIDialogDisclaimer from "@common/components/AIDialogDisclaimer.tsx";
import { useAiSpendingWarning } from "../../../aiAdministration/AiAdministration.hooks.ts";
import { isAiSpendingExhausted } from "../../../aiAdministration/AiAdministration.types.ts";

export interface BestPracticeCheckResultDialogProps {
    documentId: string;
    initialResult?: BestPracticeCheckViewModel;
    onDialogClose: () => void;
}

function BestPracticeCheckResultDialog(props: BestPracticeCheckResultDialogProps) {
    const { translateString } = useTranslation();
    const queryClient = useDotLegalQueryClient();
    const {
        isLoading,
        result,
        selectedVersionId,
        setSelectedVersionId,
        hasCheck,
        selectedVersionIsTooLargeForAi,
        showVersionDropdown,
        versionOptions,
        versionsUrl,
    } = useBestPracticeCheckResultDialog(props);

    const { selectedTemplateId, setSelectedTemplateId, templateOptions, isLoadingTemplates, onOkClick, isComparing } = useBestPracticeCheckDialog({
        documentId: props.documentId,
        documentVersionId: selectedVersionId ?? undefined,
        onCheckComplete: () => queryClient.refetchQueries(versionsUrl),
    });
    const { data: spendingWarning } = useAiSpendingWarning();
    const isAiExhausted = isAiSpendingExhausted(spendingWarning);

    const isViewingResults = hasCheck && !!result;

    const getVersionSelector = () => {
        return showVersionDropdown ? (
            <Box sx={{ minWidth: 350 }}>
                <DotLegalSelect
                    options={versionOptions}
                    selectedItem={selectedVersionId}
                    onChange={(value) => setSelectedVersionId(value)}
                    label={translateString("documentVersion")}
                    noOptionsLabel={translateString("noOptions")}
                    disableClearable
                />
            </Box>
        ) : undefined;
    };

    return (
        <DotLegalDialog
            size={"lg"}
            isLoading={isLoading}
            buttonOkText={isViewingResults ? translateString("close") : translateString("compare")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={isViewingResults ? props.onDialogClose : onOkClick}
            okButtonDisabled={!isViewingResults && (!selectedTemplateId || isComparing || isAiExhausted || selectedVersionIsTooLargeForAi)}
            inProgress={isComparing}
            fullHeight
            header={translateString("aiQualityScan")}
            footer={<AIDialogDisclaimer />}
        >
            {hasCheck && result && <BestPracticeCheckResultView result={result} headerContent={getVersionSelector()} />}

            {!hasCheck && (
                <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>{getVersionSelector()}</Box>

                    <Box sx={{ marginTop: 2 }}>
                        <BestPracticeCheckDialogContent
                            selectedTemplateId={selectedTemplateId}
                            setSelectedTemplateId={setSelectedTemplateId}
                            templateOptions={templateOptions}
                            isComparing={isComparing}
                            isLoadingTemplates={isLoadingTemplates}
                            spendingWarning={spendingWarning}
                        />
                    </Box>

                    {selectedVersionIsTooLargeForAi && (
                        <Box sx={{ marginTop: 2 }}>
                            <DotLegalAlert severity="warning" warningText={translateString("textExtractionErrorFileTooLarge")} />
                        </Box>
                    )}
                </>
            )}
        </DotLegalDialog>
    );
}

export default BestPracticeCheckResultDialog;
